import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GroupsContext } from '../../context/group/GroupContext';
import Message from '../../components/ui/Message';

export interface Resource {
    resource_id: number;
    resource_code: string;
    resource_name: string;
    resource_description: string;
    parent_resource_id?: number | null;
    order?: number;
}

interface LocationState {
    group: {
        group_id: number;
        group_name: string;
    };
}

export const GroupResourcePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { group } = location.state as LocationState;
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const { loadResources, loadGroupResources, saveGroupResources } = useContext(GroupsContext);

    const [resources, setResources] = useState<Resource[]>([]);
    const [selectedResources, setSelectedResources] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        loadResources()
            .then(res => {
                setResources(res);
            });
        loadGroupResources(group.group_id)
            .then(res => {

                let resourcesId = [];

                for (let resource of res) {
                    resourcesId.push(resource.resource_id);
                }

                setSelectedResources(resourcesId);
            })
            .finally(() => setLoading(false));
    }, []);

    

      const removeError = () => setErrorMessage('');

    const removeSuccessMessage = () => {
        setSuccessMessage('');
        navigate(-1);
    };

    /* =========================
       Helpers jerarquía
    ========================== */

    const isRoot = (r: Resource) =>
        r.parent_resource_id === null ||
        r.parent_resource_id === undefined ||
        r.parent_resource_id === 0;

    const getChildren = (parentId: number): Resource[] =>
        resources.filter(r => r.parent_resource_id === parentId);

    const getAllChildrenIds = (parentId: number): number[] =>
        getChildren(parentId).flatMap(child => [
            child.resource_id,
            ...getAllChildrenIds(child.resource_id)
        ]);

    /* =========================
       Sincronizar padres
    ========================== */

    const syncParents = (selected: number[]): number[] => {
        let result = [...selected];

        resources.forEach(resource => {
            const children = getChildren(resource.resource_id);
            if (children.length === 0) return;

            const anyChildSelected = children.some(c =>
                result.includes(c.resource_id)
            );

            if (anyChildSelected && !result.includes(resource.resource_id)) {
                result.push(resource.resource_id);
            }

            if (!anyChildSelected && result.includes(resource.resource_id)) {
                result = result.filter(id => id !== resource.resource_id);
            }
        });

        return Array.from(new Set(result));
    };

    /* =========================
       Toggle recurso
    ========================== */

    const handleToggleResource = (resource: Resource) => {
        const isSelected = selectedResources.includes(resource.resource_id);
        let updated = [...selectedResources];

        if (!isSelected) {
            updated.push(resource.resource_id);
            updated.push(...getAllChildrenIds(resource.resource_id));
        } else {
            updated = updated.filter(id => id !== resource.resource_id);
            updated = updated.filter(
                id => !getAllChildrenIds(resource.resource_id).includes(id)
            );
        }

        setSelectedResources(syncParents(updated));
    };

    /* =========================
       Render jerárquico
    ========================== */

    const renderRows = (
        parentId?: number,
        level: number = 0
    ): JSX.Element[] => {
        const items = parentId === undefined
            ? resources.filter(isRoot)
            : resources.filter(r => r.parent_resource_id === parentId);

        return items
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .flatMap(resource => [
                <tr key={resource.resource_id}>
                    <td className="text-center">
                        <input
                            type="checkbox"
                            checked={selectedResources.includes(resource.resource_id)}
                            onChange={() => handleToggleResource(resource)}
                        />
                    </td>

                    <td>{resource.resource_code}</td>

                    <td>
                        <span style={{ marginLeft: level * 20 }}>
                            {level > 0 && '   '}
                            {resource.resource_name}
                        </span>
                    </td>

                    <td className="text-muted">
                        {resource.resource_description}
                    </td>
                </tr>,

                ...renderRows(resource.resource_id, level + 1)
            ]);
    };

    /* =========================
       Guardar
    ========================== */

    const handleSave = async () => {
        const payload = {
            group_id: group.group_id,
            resources: selectedResources
        };

        const saved = await saveGroupResources(payload)

        if(saved == 'success'){
            setSuccessMessage("Permisos asignados correctamente.");
        }else{
         setErrorMessage("Ocurrió un error asignando los permisos.");   
        }
        
        
    };

    return (
        <div className="mt-5 container">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5>
                    Permisos del grupo:{' '}
                    <span className="text-bold">{group.group_name}</span>
                </h5>

                
            </div>

            {/* Tabla */}
            {loading ? (
                <div className="text-center mt-4">
                    <span className="spinner-border" />
                </div>
            ) : (
                <table className="table table-hover table-striped">
                    <thead className="thead-dark">
                        <tr>
                            <th style={{ width: 120, textAlign: 'center' }}>
                                Asignado
                            </th>
                            <th>Código</th>
                            <th>Recurso</th>
                            <th>Descripción</th>
                        </tr>
                    </thead>

                    <tbody>
                        {resources.length > 0 ? (
                            renderRows()
                        ) : (
                            <tr>
                                <td colSpan={4} className="text-center">
                                    No existen recursos.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}

            {/* Guardar */}
            <div className="d-flex justify-content-center mt-4">
                <button
                    className="btn btn-outline-secondary me-5"
                    onClick={() => navigate(-1)}
                >
                    Volver
                </button>

                <button
                    className="btn btn-success"
                    onClick={handleSave}
                >
                    Guardar cambios
                </button>
            </div>

            {errorMessage && (
                <Message
                    type="error"
                    message={errorMessage}
                    closeDialog={removeError}
                    handleOk={removeError}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}

            {successMessage && (
                <Message
                    type="success"
                    title="Éxito"
                    message={successMessage}
                    closeDialog={removeSuccessMessage}
                    handleOk={removeSuccessMessage}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    );
};
